import { Controller } from "@hotwired/stimulus";
import {
  checkOnlineStatus,
  quickCheckOnlineStatus,
} from "../onlineStatus.js";
import { ensureOfflineSanitationFormsReady } from "../offlineSanitationForms.js";
import {
  navigateToRouteDocument,
  setRouteStartButtonLoading,
} from "../routeNavigation.js";

const DATE_FILTER_NAMES = ["q[due_at_gteq]", "q[due_at_lteq]"];

const MULTI_FILTER_NAMES = [
  "q[sanitation_form_id_in][]",
  "q[lot_id_in][]",
  "q[sanitation_monitor_id_in][]",
  "q[status_in][]",
];

const DISABLED_CLASSES = ["pointer-events-none"];
const QUICK_FILTER_ACTIVE_CLASSES = ["btn-primary"];
const QUICK_FILTER_INACTIVE_CLASSES = ["btn-outline"];
const ROUTE_CARD_SELECTOR = "[data-route-calendar-route-card='true']";
const OFFLINE_DISABLED_LINK_SELECTOR =
  "[data-route-calendar-offline-disabled-link='true']";
const QUICK_START_LINK_SELECTOR =
  "[data-route-calendar-quick-start-link='true']";

export function collectOfflineFilters(params) {
  return {
    sanitationFormIds: presentValues(
      params.getAll("q[sanitation_form_id_in][]"),
    ),
    lotIds: presentValues(params.getAll("q[lot_id_in][]")),
    monitorIds: presentValues(params.getAll("q[sanitation_monitor_id_in][]")),
    statuses: presentValues(params.getAll("q[status_in][]")),
    dueAtGteq: presentValue(params.get("q[due_at_gteq]")),
    dueAtLteq: presentValue(params.get("q[due_at_lteq]")),
  };
}

export function routeMatchesOfflineFilters(route, filters) {
  if (route.launchable !== "true") return false;

  if (!matchesSelected(route.sanitationFormId, filters.sanitationFormIds))
    return false;
  if (!matchesSelected(route.lotId, filters.lotIds)) return false;
  if (!matchesSelected(route.monitorId, filters.monitorIds)) return false;
  if (!matchesSelected(route.status, filters.statuses)) return false;
  // Calendar window filters by placement day (completion day if completed, else due day)
  if (!matchesDateRange(route.calendarDay || route.dueDate, filters.dueAtGteq, filters.dueAtLteq))
    return false;

  return true;
}

export function applyOfflineRouteFilters(routeCards, filters) {
  routeCards.forEach((card) => {
    card.hidden = !routeMatchesOfflineFilters(card.dataset, filters);
  });
}

export function activeOfflineFilterCount(filters) {
  return [
    filters.sanitationFormIds,
    filters.lotIds,
    filters.monitorIds,
    filters.statuses,
    filters.dueAtGteq,
    filters.dueAtLteq,
  ].reduce((count, value) => {
    if (Array.isArray(value)) return count + value.length;
    return value ? count + 1 : count;
  }, 0);
}

export function showAllRouteCards(routeCards) {
  routeCards.forEach((card) => {
    card.hidden = false;
  });
}

export function shouldHandleOfflineFilterNavigation({
  online,
  insideFilterShell,
}) {
  return !online && insideFilterShell;
}

export function shouldDisableOfflineNavigation({ online, offlineDisabled }) {
  return !online && offlineDisabled;
}

export function shouldHandleQuickStartNavigation({ quickStart }) {
  return quickStart;
}

export function preventOfflineNavigation(event) {
  event.preventDefault();
  event.stopPropagation();
}

export function setOfflineDisabledState(link, disabled) {
  if (disabled) {
    if (
      !Object.prototype.hasOwnProperty.call(
        link.dataset,
        "routeCalendarOriginalTabindex",
      )
    ) {
      link.dataset.routeCalendarOriginalTabindex =
        link.getAttribute("tabindex") || "";
    }

    link.setAttribute("aria-disabled", "true");
    link.setAttribute("tabindex", "-1");
    DISABLED_CLASSES.forEach((klass) => link.classList.add(klass));
    return;
  }

  link.removeAttribute("aria-disabled");
  DISABLED_CLASSES.forEach((klass) => link.classList.remove(klass));

  const originalTabindex = link.dataset.routeCalendarOriginalTabindex;
  if (originalTabindex) {
    link.setAttribute("tabindex", originalTabindex);
  } else {
    link.removeAttribute("tabindex");
  }
  delete link.dataset.routeCalendarOriginalTabindex;
}

export function syncFormControlsFromUrl(form, url) {
  const params = url.searchParams;

  DATE_FILTER_NAMES.forEach((name) => {
    const input = form.querySelector(`[name="${name}"]`);
    if (input) input.value = params.get(name) || "";
  });

  MULTI_FILTER_NAMES.forEach((name) => {
    const selectedValues = params.getAll(name);
    form.querySelectorAll(`[name="${name}"]`).forEach((input) => {
      input.checked = selectedValues.includes(input.value);
    });
  });
}

export function routeStartPayloadFromCard(card) {
  const dataset = card.dataset;

  return {
    farmId: Number(dataset.farmId),
    sanitationFormId: Number(dataset.sanitationFormId),
    lot: {
      id: Number(dataset.lotId),
      name: dataset.lotName,
      total_trees: Number(dataset.lotTotalTrees),
    },
    monitorId: dataset.monitorId,
    monitorName: dataset.monitorName,
  };
}

export function offlineQuickStartHref(href) {
  const url = new URL(href, window.location.href);
  url.search = "";

  return url.toString();
}

export function offlineQuickStartHrefWhenReady(href, ready) {
  if (!ready) return null;

  return offlineQuickStartHref(href);
}

export function offlineQuickStartDestinationOrToast({
  href,
  ready,
  flash,
  unavailableMessage,
  documentRef = document,
}) {
  const destination = offlineQuickStartHrefWhenReady(href, ready);
  if (destination) return destination;

  appendOfflineFilterToast(flash, unavailableMessage, documentRef);
  return null;
}

export { setRouteStartButtonLoading as setQuickStartLoading };

export function closeDropdownForElement(element) {
  const dropdown = element?.closest?.(".dropdown");
  if (!dropdown) return false;

  if (
    typeof document !== "undefined" &&
    dropdown.contains(document.activeElement)
  ) {
    document.activeElement.blur();
  }

  return true;
}

export function syncQuickFilterSelection(filterShell, selectedLink) {
  if (!filterShell || !selectedLink) return false;

  const quickFilterLinks = Array.from(
    filterShell.querySelectorAll("[data-route-calendar-quick-filter='true']"),
  );
  if (!quickFilterLinks.includes(selectedLink)) return false;

  quickFilterLinks.forEach((link) => {
    const selected = link === selectedLink;
    QUICK_FILTER_ACTIVE_CLASSES.forEach((klass) =>
      link.classList.toggle(klass, selected),
    );
    QUICK_FILTER_INACTIVE_CLASSES.forEach((klass) =>
      link.classList.toggle(klass, !selected),
    );
  });

  return true;
}

export function appendOfflineFilterToast(
  flash,
  message,
  documentRef = document,
) {
  if (!flash || !message) return false;

  flash
    .querySelectorAll("[data-route-calendar-toast='true']")
    .forEach((toast) => toast.remove());

  const alert = documentRef.createElement("div");
  alert.className = "alert";
  alert.dataset.controller = "removals";
  alert.dataset.routeCalendarToast = "true";

  const icon = documentRef.createElement("i");
  icon.className = "w-6 h-6 i-ph-check-circle-fill text-primary";

  const text = documentRef.createElement("span");
  text.textContent = message;

  alert.appendChild(icon);
  alert.appendChild(text);
  flash.appendChild(alert);

  return true;
}

export function syncOfflineFilterIndicator(
  filterShell,
  filters,
  messageTemplate,
  documentRef = document,
) {
  if (!filterShell) return false;

  const count = activeOfflineFilterCount(filters);
  clearOfflineFilterIndicator(filterShell);
  if (count === 0) return false;

  const indicator = documentRef.createElement("div");
  indicator.className = "mt-3 text-sm alert alert-info alert-soft";
  indicator.dataset.routeCalendarOfflineFilterIndicator = "true";

  const icon = documentRef.createElement("i");
  icon.className = "w-4 h-4 i-ph-funnel";

  const text = documentRef.createElement("span");
  text.textContent = messageTemplate.replace("%{count}", count);

  indicator.appendChild(icon);
  indicator.appendChild(text);
  filterShell.appendChild(indicator);

  return true;
}

export function clearOfflineFilterIndicator(filterShell) {
  if (!filterShell) return false;

  const indicators = filterShell.querySelectorAll(
    "[data-route-calendar-offline-filter-indicator='true']",
  );
  indicators.forEach((indicator) => indicator.remove());

  return indicators.length > 0;
}

// Connects to data-controller="route-calendar"
export default class extends Controller {
  static values = {
    offlineFiltersAppliedMessage: String,
    offlineFiltersActiveMessage: String,
    offlineRouteUnavailableMessage: String,
    routeNotFullyDownloadedMessage: String,
    routeSyncFailedMessage: String,
  };

  connect() {
    this.online = true;
    this.filterShell = this.element.querySelector("#tour-filters");
    this.boundSubmit = this.submit.bind(this);
    this.boundClick = this.click.bind(this);
    this.boundOnlineStatusChange = this.refreshOnlineState.bind(this);

    this.element.addEventListener("submit", this.boundSubmit, true);
    this.element.addEventListener("click", this.boundClick, true);
    window.addEventListener("online", this.boundOnlineStatusChange);
    window.addEventListener("offline", this.boundOnlineStatusChange);

    this.refreshOnlineState();
  }

  disconnect() {
    this.element.removeEventListener("submit", this.boundSubmit, true);
    this.element.removeEventListener("click", this.boundClick, true);
    window.removeEventListener("online", this.boundOnlineStatusChange);
    window.removeEventListener("offline", this.boundOnlineStatusChange);
  }

  submit(event) {
    if (!this.#filterForm(event.target)) return;
    if (
      !shouldHandleOfflineFilterNavigation({
        online: this.online,
        insideFilterShell: true,
      })
    )
      return;

    preventOfflineNavigation(event);
    this.#applyFormFilters(event.target);
    this.#finishOfflineFilterInteraction(
      event.submitter || document.activeElement,
    );
  }

  async click(event) {
    const submitter = event.target.closest(
      "button[type='submit'], input[type='submit']",
    );
    if (submitter && this.filterShell?.contains(submitter)) {
      if (
        shouldHandleOfflineFilterNavigation({
          online: this.online,
          insideFilterShell: true,
        })
      ) {
        preventOfflineNavigation(event);
        if (submitter.form) this.#applyFormFilters(submitter.form);
        this.#finishOfflineFilterInteraction(submitter);
        return;
      }
    }

    const link = event.target.closest("a");
    if (!link || !this.element.contains(link)) return;

    const filterLink = this.filterShell?.contains(link);
    const offlineDisabled = link.matches(OFFLINE_DISABLED_LINK_SELECTOR);
    const quickStart = link.matches(QUICK_START_LINK_SELECTOR);

    if (
      shouldHandleOfflineFilterNavigation({
        online: this.online,
        insideFilterShell: filterLink,
      })
    ) {
      preventOfflineNavigation(event);
      this.#applyFilterLink(link);
      syncQuickFilterSelection(this.filterShell, link);
      this.#finishOfflineFilterInteraction(link, { closeDropdown: false });
      return;
    }

    if (shouldHandleQuickStartNavigation({ quickStart })) {
      preventOfflineNavigation(event);
      await this.#startRoute(link);
      return;
    }

    if (
      shouldDisableOfflineNavigation({ online: this.online, offlineDisabled })
    ) {
      preventOfflineNavigation(event);
    }
  }

  async refreshOnlineState() {
    this.online = await checkOnlineStatus();
    this.updateOfflineState();
  }

  updateOfflineState() {
    const offline = !this.online;
    this.offlineDisabledLinks.forEach((link) =>
      setOfflineDisabledState(link, offline),
    );

    if (offline) {
      this.#applyCurrentFilters();
    } else {
      clearOfflineFilterIndicator(this.filterShell);
      showAllRouteCards(this.routeCards);
    }
  }

  #applyFilterLink(link) {
    const form = this.filterShell?.querySelector("form");
    if (!form) return;

    syncFormControlsFromUrl(form, new URL(link.href, window.location.href));
    this.#applyFormFilters(form);
  }

  #applyCurrentFilters() {
    const form = this.filterShell?.querySelector("form");
    if (!form) return;

    this.#applyFormFilters(form);
  }

  #applyFormFilters(form) {
    const filters = collectOfflineFilters(new FormData(form));
    applyOfflineRouteFilters(this.routeCards, filters);
    if (!this.online) {
      syncOfflineFilterIndicator(
        this.filterShell,
        filters,
        this.offlineFiltersActiveMessageValue,
      );
    } else {
      clearOfflineFilterIndicator(this.filterShell);
    }
  }

  #finishOfflineFilterInteraction(
    sourceElement,
    { closeDropdown = true } = {},
  ) {
    if (closeDropdown) closeDropdownForElement(sourceElement);
    appendOfflineFilterToast(
      document.getElementById("flash"),
      this.offlineFiltersAppliedMessageValue,
    );
  }

  async #startRoute(link) {
    const routeCard = link.closest(ROUTE_CARD_SELECTOR);
    if (!routeCard) return;

    setOfflineDisabledState(link, true);
    setRouteStartButtonLoading(link, true);

    let online = false;
    let navigationTriggered = false;
    let enableLink = false;

    try {
      online = await quickCheckOnlineStatus();
      this.online = online;

      const result = await ensureOfflineSanitationFormsReady({
        farmId: routeCard.dataset.farmId,
        formId: routeCard.dataset.sanitationFormId,
        online,
      });

      if (!result.ready) {
        this.#showRouteNotFullyDownloadedMessage();
        enableLink = online !== false;
        return;
      }

      const destination = offlineQuickStartHref(link.href);
      const navigationResult = await navigateToRouteDocument(destination, {
        checkOnline: async () => online,
        historyMode: "push",
      });

      navigationTriggered = navigationResult.allowed;
      enableLink = navigationResult.allowed || online !== false;

      if (!navigationResult.allowed) this.#showRouteNotFullyDownloadedMessage();
    } catch (error) {
      console.error(
        "Error checking route calendar quick-start readiness:",
        error,
      );
      if (online !== false && this.hasRouteSyncFailedMessageValue) {
        appendOfflineFilterToast(
          document.getElementById("flash"),
          this.routeSyncFailedMessageValue,
        );
      } else {
        this.#showRouteNotFullyDownloadedMessage();
      }
      enableLink = online !== false;
    } finally {
      if (!navigationTriggered) {
        setRouteStartButtonLoading(link, false);
        setOfflineDisabledState(link, !enableLink);
      }
    }
  }

  #showRouteNotFullyDownloadedMessage() {
    appendOfflineFilterToast(
      document.getElementById("flash"),
      this.routeNotFullyDownloadedMessage,
    );
  }

  get routeNotFullyDownloadedMessage() {
    return this.hasRouteNotFullyDownloadedMessageValue
      ? this.routeNotFullyDownloadedMessageValue
      : this.offlineRouteUnavailableMessageValue;
  }

  #filterForm(form) {
    return form && this.filterShell?.contains(form);
  }

  get routeCards() {
    return Array.from(this.element.querySelectorAll(ROUTE_CARD_SELECTOR));
  }

  get offlineDisabledLinks() {
    return Array.from(
      this.element.querySelectorAll(OFFLINE_DISABLED_LINK_SELECTOR),
    );
  }
}

function matchesSelected(value, selectedValues) {
  return selectedValues.length === 0 || selectedValues.includes(value);
}

function matchesDateRange(value, gteq, lteq) {
  if (!gteq && !lteq) return true;
  if (!value) return false;
  if (gteq && value < gteq) return false;
  if (lteq && value > lteq) return false;

  return true;
}

function presentValue(value) {
  return value || null;
}

function presentValues(values) {
  return values.filter((value) => value);
}
